import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Hero */}
      <section className="pt-28 pb-14 lg:pt-36 lg:pb-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/loomscontact.jpg"
            alt="Veltrix Apparel contact"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/75" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/80 mb-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-white/60">/</span>
            <span className="text-white">Contact</span>
          </nav>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            Get in touch
          </h1>
          <p className="mt-6 text-lg text-white max-w-2xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]">
            Need a fitting, custom order, or bulk team kit? We're based in Manhattan and ready to help.
          </p>
          <div className="mt-12 flex flex-wrap gap-8 text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
            <span>24h response</span>
            <span>Free consultation</span>
          </div>
        </div>
      </section>

      {/* Contact + Form */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <h2 className="font-display text-2xl font-semibold text-slate-900 mb-4">Let's connect</h2>
              <p className="text-slate-600 mb-10">
                Whether you need a suit fitting, custom jerseys, or bulk team kits, we're here. Reach out or visit us in Manhattan.
              </p>
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Visit us</h3>
                    <p className="text-slate-600 mt-0.5">Manhattan, New York, NY</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Call</h3>
                    <a href="tel:+17184279036" className="text-slate-600 mt-0.5 hover:text-slate-900">(718) 427-9036</a>
                    <p className="text-sm text-slate-500 mt-1">Mon–Fri, 9AM–6PM EST</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0 text-slate-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900">Email</h3>
                    <a href="mailto:hello@veltrixapparel.com" className="text-slate-600 mt-0.5 hover:text-slate-900">hello@veltrixapparel.com</a>
                    <p className="text-sm text-slate-500 mt-1">We respond within 24 hours</p>
                  </div>
                </div>
              </div>
              <div className="mt-10">
                <h3 className="font-medium text-slate-900 mb-3">Follow us</h3>
                <div className="flex gap-3">
                  {['Instagram', 'LinkedIn'].map((name) => (
                    <a key={name} href="#" aria-label={name} className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors">
                      <span className="text-sm font-medium">{name.slice(0, 1)}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-10 shadow-sm">
              <h3 className="font-display text-xl font-semibold text-slate-900 mb-6">Send a message</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-shadow resize-none"
                    placeholder="How can we help?"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3.5 rounded-lg bg-slate-900 text-white font-medium text-sm hover:bg-slate-800 transition-colors"
                >
                  Send message
                </button>
              </form>
              <p className="mt-4 text-xs text-slate-500 text-center">
                By submitting you agree to our{' '}
                <a href="#" className="underline hover:text-slate-700">privacy policy</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-14 lg:py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-semibold text-slate-900 mb-6">Find us</h2>
          <div className="aspect-video rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.25436351647!2d-74.11976404999999!3d40.69766374999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2sus!4v1703123456789!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Veltrix Apparel - Manhattan, New York, NY"
              className="w-full h-full"
            />
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-sm font-medium text-slate-900">Parking</div>
              <div className="text-sm text-slate-600 mt-0.5">Convenient parking for visitors</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-sm font-medium text-slate-900">Accessible</div>
              <div className="text-sm text-slate-600 mt-0.5">Fully accessible space</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
              <div className="text-sm font-medium text-slate-900">Refreshments</div>
              <div className="text-sm text-slate-600 mt-0.5">Coffee & tea for guests</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
